'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { Panel } from '@/components/ui/panel';
import { D9_SCALE } from '@/config/solana';
import { useFormattedCurrency } from '@/lib/currency';
import { cn } from '@/lib/ui/utils';
import { useChartSettingsStore } from '@/stores/chart-settings';
import { SignedBps } from './signed-bps';
import { StatTile } from './winner-stat-tile';
const holderCountFormatterCache: Map<string, Intl.NumberFormat> = new Map();
function getHolderCountFormatter(locale: string): Intl.NumberFormat {
  const cached = holderCountFormatterCache.get(locale);
  if (cached !== undefined) return cached;
  const formatter = new Intl.NumberFormat(locale);
  holderCountFormatterCache.set(locale, formatter);
  return formatter;
}
export interface WinnerTokenStatsSnapshot {
  marketCap: string | null;
  /** Spot price (D9). Shown in the first tile when the chart's Price/Market Cap
   *  toggle is on Price; the tile shows market cap otherwise. */
  price: string | null;
  volumeTotal: string;
  liquidity: string;
  holderCount: number;
  change1hBps: string | null;
  change24hBps: string | null;
}

/**
 * AMM token-stat tiles for the winner page right column. Values come from the
 * graduated-row snapshot the orchestrator fetches (detail surfaces read the row
 * directly — the per-field `useGet*Field` leaf is `<Items>`-only). This widget's
 * layout omits 15m-change and Supply (the graduated `TokenCta` card surfaces 15m
 * instead); the signed-change renderer is the shared `SignedBps`.
 */
export function WinnerTokenStats(t0) {
  const $ = _c(65);
  const {
    snapshot,
    className
  } = t0;
  const t = useTranslations("market.winnerDetail");
  const locale = useLocale();
  const {
    formatD6,
    formatBalance
  } = useFormattedCurrency();
  const isPrice = useChartSettingsStore(_temp) === "price";
  const {
    marketCap,
    price,
    volumeTotal,
    liquidity,
    holderCount,
    change1hBps,
    change24hBps
  } = snapshot;
  let t1;
  if ($[0] !== formatD6) {
    t1 = n => formatD6(String(Math.round(n)), "compact");
    $[0] = formatD6;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const formatMoney = t1;
  let t2;
  if ($[2] !== holderCount || $[3] !== locale) {
    const t3 = getHolderCountFormatter(locale);
    let t4;
    if ($[5] !== holderCount) {
      t4 = Math.round(Number(holderCount));
      $[5] = holderCount;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    t2 = t3.format(Math.max(0, t4));
    $[2] = holderCount;
    $[3] = locale;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const holders = t2;
  let t3;
  if ($[7] !== className) {
    t3 = cn("grid grid-cols-3 gap-2 p-2", className);
    $[7] = className;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== isPrice || $[10] !== t) {
    t4 = isPrice ? t("statPrice") : t("statMarketCap");
    $[9] = isPrice;
    $[10] = t;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== formatBalance || $[13] !== formatD6 || $[14] !== formatMoney || $[15] !== isPrice || $[16] !== marketCap || $[17] !== price) {
    t5 = (isPrice ? price : marketCap) === null ? <span><span className="sr-only">Unavailable</span><span aria-hidden="true">{isPrice ? formatBalance(0, "price") : formatD6("0", "compact")}</span></span> : <AnimatedNumber value={Number(isPrice ? price : marketCap)} format={isPrice ? n_0 => formatBalance(n_0 / D9_SCALE, "price") : formatMoney} animateWithColour={true} duration={400} />;
    $[12] = formatBalance;
    $[13] = formatD6;
    $[14] = formatMoney;
    $[15] = isPrice;
    $[16] = marketCap;
    $[17] = price;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== t4 || $[20] !== t5) {
    t6 = <StatTile label={t4}>{t5}</StatTile>;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== t) {
    t7 = t("statVolumeTotal");
    $[22] = t;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== formatD6 || $[25] !== volumeTotal) {
    t8 = formatD6(volumeTotal, "compact");
    $[24] = formatD6;
    $[25] = volumeTotal;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t7 || $[28] !== t8) {
    t9 = <StatTile label={t7}>{t8}</StatTile>;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== t) {
    t10 = t("statLiquidity");
    $[30] = t;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  const t11 = Number(liquidity);
  let t12;
  if ($[32] !== formatMoney || $[33] !== t11) {
    t12 = <AnimatedNumber value={t11} format={formatMoney} animateWithColour={true} duration={400} />;
    $[32] = formatMoney;
    $[33] = t11;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== t10 || $[36] !== t12) {
    t13 = <StatTile label={t10}>{t12}</StatTile>;
    $[35] = t10;
    $[36] = t12;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== t) {
    t14 = t("statHolders");
    $[38] = t;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== holders || $[41] !== t14) {
    t15 = <StatTile label={t14}>{holders}</StatTile>;
    $[40] = holders;
    $[41] = t14;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t) {
    t16 = t("statChange1h");
    $[43] = t;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== change1hBps) {
    t17 = <SignedBps bps={change1hBps} />;
    $[45] = change1hBps;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== t16 || $[48] !== t17) {
    t18 = <StatTile label={t16}>{t17}</StatTile>;
    $[47] = t16;
    $[48] = t17;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== t) {
    t19 = t("statChange24h");
    $[50] = t;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== change24hBps) {
    t20 = <SignedBps bps={change24hBps} />;
    $[52] = change24hBps;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] !== t19 || $[55] !== t20) {
    t21 = <StatTile label={t19}>{t20}</StatTile>;
    $[54] = t19;
    $[55] = t20;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== t13 || $[58] !== t15 || $[59] !== t18 || $[60] !== t21 || $[61] !== t3 || $[62] !== t6 || $[63] !== t9) {
    t22 = <Panel className={t3}>{t6}{t9}{t13}{t15}{t18}{t21}</Panel>;
    $[57] = t13;
    $[58] = t15;
    $[59] = t18;
    $[60] = t21;
    $[61] = t3;
    $[62] = t6;
    $[63] = t9;
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  return t22;
}
function _temp(s) {
  return s.yAxisProperty;
}